/**
 * Simple GEDCOM parser for family tree visualization
 */

/**
 * Parse GEDCOM file content into structured data
 * @param {string} gedcomContent - Raw GEDCOM file content
 * @returns {Object} Parsed family tree data
 */
export const parseGedcomToTree = (gedcomContent) => {
  try {
    const lines = gedcomContent.split('\n').map(line => line.trim()).filter(line => line);
    const individuals = {};
    const families = {};
    
    let currentRecord = null;
    let currentSubRecord = null;
    
    for (const line of lines) {
      const parts = line.split(' ');
      const level = parseInt(parts[0]);
      const tag = parts[1];
      const data = parts.slice(2).join(' ');
      
      if (level === 0) {
        // New top-level record
        if (tag.startsWith('@') && tag.endsWith('@')) {
          // This is a pointer/ID
          const id = tag;
          const recordType = parts[2];
          
          if (recordType === 'INDI') {
            currentRecord = {
              id: id,
              type: 'INDI',
              name: '',
              birth: '',
              death: '',
              sex: '',
              families: [],
              parents: null
            };
            individuals[id] = currentRecord;
          } else if (recordType === 'FAM') {
            currentRecord = {
              id: id,
              type: 'FAM',
              husband: null,
              wife: null,
              children: [],
              marriage: ''
            };
            families[id] = currentRecord;
          } else {
            currentRecord = null;
          }
        }
        currentSubRecord = null;
      } else if (level === 1 && currentRecord) {
        // First level under a record
        currentSubRecord = { tag, data };
        
        switch (tag) {
          case 'NAME':
            if (currentRecord.type === 'INDI') {
              currentRecord.name = data.replace(/\//g, '');
            }
            break;
          case 'SEX':
            if (currentRecord.type === 'INDI') {
              currentRecord.sex = data;
            }
            break;
          case 'FAMC':
            if (currentRecord.type === 'INDI') {
              currentRecord.parents = data;
            }
            break;
          case 'FAMS':
            if (currentRecord.type === 'INDI') {
              currentRecord.families.push(data);
            }
            break;
          case 'HUSB':
            if (currentRecord.type === 'FAM') {
              currentRecord.husband = data;
            }
            break;
          case 'WIFE':
            if (currentRecord.type === 'FAM') {
              currentRecord.wife = data;
            }
            break;
          case 'CHIL':
            if (currentRecord.type === 'FAM') {
              currentRecord.children.push(data);
            }
            break;
          default:
            // Other tags are ignored for now
            break;
        }
      } else if (level === 2 && currentSubRecord) {
        // Second level - usually dates and places
        if (tag === 'DATE') {
          if (currentSubRecord.tag === 'BIRT' && currentRecord.type === 'INDI') {
            currentRecord.birth = data;
          } else if (currentSubRecord.tag === 'DEAT' && currentRecord.type === 'INDI') {
            currentRecord.death = data;
          } else if (currentSubRecord.tag === 'MARR' && currentRecord.type === 'FAM') {
            currentRecord.marriage = data;
          }
        }
      }
    }
    
    return { individuals, families };
  } catch (error) {
    console.error('Error parsing GEDCOM:', error);
    return { individuals: {}, families: {} };
  }
};

/**
 * Convert parsed GEDCOM data to D3 tree format
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} rootPersonId - ID of root person for tree
 * @returns {Object} D3-compatible tree structure
 */
export const convertToD3Tree = (gedcomData, rootPersonId) => {
  const { individuals, families } = gedcomData;
  
  if (!individuals[rootPersonId]) {
    console.error('Root person not found:', rootPersonId);
    return null;
  }
  
  const buildNode = (personId, visited = new Set()) => {
    if (visited.has(personId) || !individuals[personId]) {
      return null;
    }
    
    visited.add(personId);
    const person = individuals[personId];
    
    const node = {
      name: person.name || 'Unknown',
      attributes: {
        birth: person.birth,
        death: person.death,
        sex: person.sex,
        id: personId
      },
      children: []
    };
    
    // Add children from all families where this person is a parent
    person.families.forEach(familyId => {
      const family = families[familyId];
      if (family && (family.husband === personId || family.wife === personId)) {
        family.children.forEach(childId => {
          const childNode = buildNode(childId, new Set(visited));
          if (childNode) {
            node.children.push(childNode);
          }
        });
      }
    });
    
    return node;
  };
  
  return buildNode(rootPersonId);
};

/**
 * Find potential root persons (those without parents or oldest generation)
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @returns {Array} Array of potential root persons
 */
export const findRootPersons = (gedcomData) => {
  const { individuals } = gedcomData;
  
  const rootCandidates = Object.values(individuals)
    .filter(person => !person.parents || person.parents === '')
    .map(person => ({
      id: person.id,
      name: person.name || 'Unknown',
      birth: person.birth
    }))
    .sort((a, b) => {
      // Sort by birth date if available, otherwise by name
      if (a.birth && b.birth) {
        return a.birth.localeCompare(b.birth);
      }
      return a.name.localeCompare(b.name);
    });
  
  // If no clear roots found, return all individuals
  if (rootCandidates.length === 0) {
    return Object.values(individuals)
      .slice(0, 10) // Limit to first 10 to avoid overwhelming
      .map(person => ({
        id: person.id,
        name: person.name || 'Unknown',
        birth: person.birth
      }));
  }
  
  return rootCandidates;
};

/**
 * Search for persons by name
 * @param {Object} gedcomData - Parsed GEDCOM data
 * @param {string} searchTerm - Name to search for
 * @returns {Array} Array of matching persons
 */
export const searchPersons = (gedcomData, searchTerm) => {
  const { individuals } = gedcomData;
  
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const term = searchTerm.toLowerCase();
  
  return Object.values(individuals)
    .filter(person => 
      person.name && person.name.toLowerCase().includes(term)
    )
    .map(person => ({
      id: person.id,
      name: person.name || 'Unknown',
      birth: person.birth,
      death: person.death
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 20); // Limit results
};
